import { createClient } from '@supabase/supabase-js';

// Tipos para la caché
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface CacheOptions {
  ttl?: number; // Tiempo de vida en milisegundos
  staleWhileRevalidate?: boolean; // Si es true, devuelve datos obsoletos mientras se actualizan
}

// Clase para gestionar la caché de consultas
export class QueryCache {
  private static instance: QueryCache;
  private cache: Map<string, CacheEntry<any>>;
  private supabase: ReturnType<typeof createClient>;
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutos por defecto

  private constructor() {
    this.cache = new Map();
    this.supabase = createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL || '',
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''
    );
  }

  public static getInstance(): QueryCache {
    if (!QueryCache.instance) {
      QueryCache.instance = new QueryCache();
    }
    return QueryCache.instance;
  }

  // Obtener datos de la caché o de Supabase
  public async query<T>(
    key: string,
    queryFn: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    const { ttl = this.defaultTTL, staleWhileRevalidate = true } = options;
    const now = Date.now();
    const cachedEntry = this.cache.get(key);

    // Si hay datos en caché y no han expirado, devolverlos
    if (cachedEntry && cachedEntry.expiresAt > now) {
      return cachedEntry.data;
    }

    // Si hay datos obsoletos y se permite staleWhileRevalidate, devolverlos mientras se actualizan
    if (staleWhileRevalidate && cachedEntry) {
      // Actualizar en segundo plano
      this.updateCache(key, queryFn, ttl).catch(console.error);
      return cachedEntry.data;
    }

    // Si no hay datos o han expirado, obtener nuevos datos
    return this.updateCache(key, queryFn, ttl);
  }

  // Actualizar la caché con nuevos datos
  private async updateCache<T>(
    key: string,
    queryFn: () => Promise<T>,
    ttl: number
  ): Promise<T> {
    try {
      const data = await queryFn();
      const now = Date.now();
      
      this.cache.set(key, {
        data,
        timestamp: now,
        expiresAt: now + ttl,
      });

      return data;
    } catch (error) {
      console.error(`Error updating cache for key ${key}:`, error);
      throw error;
    }
  }

  // Invalidar una entrada de la caché
  public invalidate(key: string): void {
    this.cache.delete(key);
  }

  // Invalidar múltiples entradas de la caché
  public invalidateMany(keys: string[]): void {
    keys.forEach(key => this.cache.delete(key));
  }

  // Invalidar todas las entradas de la caché
  public invalidateAll(): void {
    this.cache.clear();
  }

  // Obtener el estado de la caché
  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
} 